import { c as _c } from "react/compiler-runtime";
import ParticipationIdentityProfileCard, { type ParticipationIdentityProfileCardVariant } from "@/components/waves/drops/participation/ParticipationIdentityProfileCard";
import IdentityProfileSupplement from "@/components/waves/drops/identity/IdentityProfileSupplement";
import type { ApiDropMetadataResponse } from "@/generated/models/ApiDropMetadataResponse";
import type { ApiWaveMin } from "@/generated/models/ApiWaveMin";
import Link from "next/link";
import { getDropIdentityFallbackValue, getDropIdentityProfile } from "../identityDisplay.helpers";
export type WaveDropIdentityVariant = "full" | "compact";
interface WaveDropIdentityProps {
  readonly drop: {
    readonly id?: string | number | undefined;
    readonly wave: Pick<ApiWaveMin, "submission_type"> | null | undefined;
    readonly metadata: readonly ApiDropMetadataResponse[] | null | undefined;
  };
  readonly variant: WaveDropIdentityVariant;
  readonly cardVariant?: ParticipationIdentityProfileCardVariant | undefined;
  readonly className?: string | undefined;
  readonly testIdPrefix?: string | undefined;
}
const getIdentityHref = (value: string) => `/${encodeURIComponent(value.toLowerCase())}`;
function IdentityFallbackCard(t0) {
  const $ = _c(6);
  const {
    fallbackValue,
    testId
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="tw-text-[11px] tw-font-medium tw-uppercase tw-tracking-[0.14em] tw-text-iron-500">Identity</span>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== fallbackValue) {
    t2 = <p className="tw-mb-0 tw-mt-3 tw-break-all tw-text-sm tw-font-semibold tw-text-iron-50">{fallbackValue}</p>;
    $[1] = fallbackValue;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2 || $[4] !== testId) {
    t3 = <div data-testid={testId} className="tw-rounded-xl tw-border tw-border-iron-800/60 tw-bg-iron-900/50 tw-p-4">{t1}{t2}</div>;
    $[3] = t2;
    $[4] = testId;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
export function WaveDropIdentity(t0) {
  const $ = _c(33);
  const {
    drop,
    variant,
    cardVariant,
    className,
    testIdPrefix: t1
  } = t0;
  const testIdPrefix = t1 === undefined ? "wave-drop-identity" : t1;
  let identityProfile;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[0] !== cardVariant || $[1] !== className || $[2] !== drop.id || $[3] !== drop.metadata || $[4] !== drop.wave || $[5] !== testIdPrefix || $[6] !== variant) {
    t8 = Symbol.for("react.early_return_sentinel");
    bb0: {
      identityProfile = getDropIdentityProfile({
        wave: drop.wave,
        metadata: drop.metadata
      });
      const fallbackValue = identityProfile ? null : getDropIdentityFallbackValue({
        wave: drop.wave,
        metadata: drop.metadata
      });
      if (!identityProfile && !fallbackValue) {
        t8 = null;
        break bb0;
      }
      if (variant === "full") {
        const t9 = identityProfile ? <div data-testid={`${testIdPrefix}-full`}><ParticipationIdentityProfileCard profile={identityProfile} contextId={drop.id} variant={cardVariant} /></div> : <IdentityFallbackCard fallbackValue={fallbackValue} testId={`${testIdPrefix}-full`} />;
        let t10;
        if ($[15] !== className || $[16] !== t9) {
          t10 = <div className={className}>{t9}</div>;
          $[15] = className;
          $[16] = t9;
          $[17] = t10;
        } else {
          t10 = $[17];
        }
        t8 = t10;
        break bb0;
      }
      const displayLabel = identityProfile?.handle ?? identityProfile?.primary_address ?? fallbackValue;
      if (!displayLabel) {
        t8 = null;
        break bb0;
      }
      t7 = className;
      t5 = `${testIdPrefix}-compact`;
      t6 = "tw-flex tw-min-w-0 tw-flex-col tw-gap-2 tw-rounded-lg tw-border tw-border-iron-800/50 tw-bg-iron-900/40 tw-px-3 tw-py-2.5";
      t2 = "tw-flex tw-min-w-0 tw-items-center tw-gap-2";
      if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
        t3 = <span className="tw-flex-shrink-0 tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-iron-500">Identity</span>;
        $[18] = t3;
      } else {
        t3 = $[18];
      }
      t4 = identityProfile ? <Link href={getIdentityHref(displayLabel)} prefetch={false} onClick={_temp} className="tw-min-w-0 tw-truncate tw-text-sm tw-font-semibold tw-text-iron-100 tw-no-underline tw-transition tw-duration-300 tw-ease-out desktop-hover:hover:tw-text-iron-300">{displayLabel}</Link> : <span className="tw-min-w-0 tw-break-all tw-text-sm tw-font-semibold tw-text-iron-100">{displayLabel}</span>;
    }
    $[0] = cardVariant;
    $[1] = className;
    $[2] = drop.id;
    $[3] = drop.metadata;
    $[4] = drop.wave;
    $[5] = testIdPrefix;
    $[6] = variant;
    $[7] = identityProfile;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    identityProfile = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
    t7 = $[13];
    t8 = $[14];
  }
  if (t8 !== Symbol.for("react.early_return_sentinel")) {
    return t8;
  }
  let t9;
  if ($[19] !== t2 || $[20] !== t3 || $[21] !== t4) {
    t9 = <div className={t2}>{t3}{t4}</div>;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== identityProfile) {
    t10 = identityProfile && <IdentityProfileSupplement profile={identityProfile} variant="compact" maxRepCategories={3} />;
    $[23] = identityProfile;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10 || $[26] !== t5 || $[27] !== t6 || $[28] !== t9) {
    t11 = <div data-testid={t5} className={t6}>{t9}{t10}</div>;
    $[25] = t10;
    $[26] = t5;
    $[27] = t6;
    $[28] = t9;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== t11 || $[31] !== t7) {
    t12 = <div className={t7}>{t11}</div>;
    $[30] = t11;
    $[31] = t7;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  return t12;
}
function _temp(event) {
  return event.stopPropagation();
}